import BuildingIcon from '@/components/Icons/BuildingIcon';
import HomeIcon from '@/components/Icons/HomeIcon';
import SquareFeetIcon from '@/components/Icons/SquareFeetIcon';
import PropertyCardActions from '@/components/PropertyCardActions/PropertyCardActions';
import PropertyCardFeatures from '@/components/PropertyCardFeatures/PropertyCardFeatures';
import PropertyCardThumbnails from '@/components/PropertyCardThumbnails/PropertyCardThumbnails';
import UnitAvailableTag from '@/components/UnitAvailableTag/UnitAvailableTag';
import type { Property } from '@/domain/properties';
import { type Unit } from '@/domain/units';
import { getAddressDescription } from '@/utils/addresses';
import { formatCurrency } from '@/utils/currency';
import { getBathroomCount } from '@/utils/units';
import { type FC } from 'react';

interface Props {
  property: Property;
  unit: Unit;
  showFeatures?: boolean;
  showApplyButtons?: boolean;
}

const PropertyCard: FC<Props> = ({
  property,
  unit,
  showFeatures = false,
  showApplyButtons = false,
}) => {
  if (!property || !unit) {
    return null;
  }

  const propertyName = getAddressDescription(property.address);

  return (
    <div className="flex flex-col bg-white rounded-none overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors duration-300">
      <div className="relative h-64 group">
        <PropertyCardThumbnails property={property} unit={unit} />
        <UnitAvailableTag
          unitListingStatus={unit.listingStatus}
          className="absolute top-4 right-4"
        />
      </div>

      <div className="flex flex-col flex-1 p-6">
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">{propertyName}</h3>
        <div className="flex items-center text-gray-600 mb-4 space-x-4">
          <span className="flex items-center">
            <HomeIcon />
            {unit.bedroomCount} BD
          </span>
          <span className="flex items-center">
            <BuildingIcon />
            {getBathroomCount({
              fullBathroomCount: unit.fullBathroomCount,
              halfBathroomCount: unit.halfBathroomCount,
              quarterBathroomCount: unit.quarterBathroomCount,
              threeQuarterBathroomCount: unit.threeQuarterBathroomCount,
            })}{' '}
            BA
          </span>
          <span className="flex items-center">
            <SquareFeetIcon />
            {unit.squareFeet} SF
          </span>
        </div>

        <div className="text-3xl font-bold text-accent-600 mb-4">
          {formatCurrency(unit.listedRent)}
          <span className="text-lg text-gray-600">/mo</span>
        </div>

        {showFeatures ? <PropertyCardFeatures amenities={unit.amenityNames} /> : null}

        <div className="mt-auto" />
        <PropertyCardActions
          unitId={unit.id}
          showApplyButtons={showApplyButtons}
          unitListingStatus={unit.listingStatus}
        />
      </div>
    </div>
  );
};

export default PropertyCard;
